import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Input,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Checkbox,
  Drawer,
  Divider,
  IconButton,
  Select,
  Option,
} from '@mui/joy'
import Grid from '@mui/joy/Grid'
import {
  X as CloseOutlined,
  Phone as PhoneOutlined,
  Buildings as BusinessOutlined,
  User as PersonOutlined,
  Clock as AccessTimeOutlined,
  Note as NotesOutlined,
  CheckCircle as CheckCircleOutline,
  Circle as RadioButtonUnchecked,
} from '@phosphor-icons/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  useCallBuddyOverview,
  useCallBuddyFollowUps,
  useCallDetail,
  useToggleFollowedUp,
} from '../../hooks/useCallBuddyReports'
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable'
import type { RecentCall, LeaderboardEntry } from '../../types/callbuddy'
import type { FollowUpCall } from '../../types/callbuddy'

const OUTCOME_LABELS: Record<string, string> = {
  no_answer: 'No Answer',
  gatekeeper_block: 'Gatekeeper Block',
  meeting_set: 'Meeting Set',
  follow_up: 'Follow Up',
  disqualified: 'Disqualified',
  other: 'Other',
}

const OUTCOME_COLORS: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  meeting_set: 'success',
  follow_up: 'primary',
  no_answer: 'neutral',
  gatekeeper_block: 'warning',
  disqualified: 'danger',
  other: 'neutral',
}

const STRUCTURED_NOTE_LABELS: Record<string, string> = {
  pain: 'Pain Points',
  current_system: 'Current System',
  timeline: 'Timeline',
  budget: 'Budget',
  decision_maker: 'Decision Maker',
  next_steps: 'Next Steps',
}

const formatDuration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatDate = (iso: string) => {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatFullDate = (iso: string) => {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ─── Call Detail Drawer ─────────────────────────────────────────────────────

function CallDetailDrawer({
  callId,
  open,
  onClose,
}: {
  callId: string | null
  open: boolean
  onClose: () => void
}) {
  const { data: call, isLoading, error, refetch } = useCallDetail(callId, open)
  const toggleMutation = useToggleFollowedUp()

  const handleToggle = () => {
    if (!call) return
    toggleMutation.mutate({ id: call.id, followed_up: !call.followed_up })
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      size="md"
      slotProps={{
        content: { sx: { width: { xs: '100%', sm: 480 } } },
      }}
    >
      <Box sx={{ p: 2.5, height: '100%', overflow: 'auto' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography level="h4">Call Details</Typography>
          <IconButton aria-label="Close call details" variant="plain" onClick={onClose} size="sm">
            <CloseOutlined />
          </IconButton>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? <Alert color="danger" endDecorator={<Button onClick={()=>refetch()}>Retry</Button>}>Could not load this call.</Alert> : !call ? (
          <Alert color="neutral">No call data found.</Alert>
        ) : (
          <Stack spacing={2.5}>
            {toggleMutation.error && <Alert color="danger">The follow-up status could not be saved.</Alert>}
            {/* Outcome + Followed Up */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="lg"
                variant="soft"
                color={OUTCOME_COLORS[call.outcome || 'other'] || 'neutral'}
              >
                {OUTCOME_LABELS[call.outcome || 'other'] || call.outcome || '-'}
              </Chip>
              <Chip
                size="lg"
                variant={call.followed_up ? 'solid' : 'outlined'}
                color={call.followed_up ? 'success' : 'neutral'}
                startDecorator={call.followed_up ? <CheckCircleOutline /> : <RadioButtonUnchecked />}
                disabled={toggleMutation.isPending}
                onClick={handleToggle}
                sx={{ cursor: 'pointer' }}
              >
                {call.followed_up ? 'Followed Up' : 'Not Followed Up'}
              </Chip>
            </Stack>

            <Divider />

            {/* Contact Info */}
            <Card variant="soft" size="sm">
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 1 }}>Contact Information</Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonOutlined size={18} color="var(--joy-palette-text-tertiary)" />
                    <Typography level="body-sm">
                      {call.contact_name || 'Unknown'}{call.contact_role ? ` - ${call.contact_role}` : ''}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BusinessOutlined size={18} color="var(--joy-palette-text-tertiary)" />
                    <Typography level="body-sm">{call.company_name || '-'}</Typography>
                  </Stack>
                  {call.contact_phone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneOutlined size={18} color="var(--joy-palette-text-tertiary)" />
                      <Typography level="body-sm">{call.contact_phone}</Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Call Timing */}
            <Card variant="soft" size="sm">
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 1 }}>Call Timing</Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeOutlined size={18} color="var(--joy-palette-text-tertiary)" />
                    <Typography level="body-sm">
                      Started: {formatFullDate(call.started_at)}
                    </Typography>
                  </Stack>
                  {call.ended_at && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeOutlined size={18} color="var(--joy-palette-text-tertiary)" />
                      <Typography level="body-sm">
                        Ended: {formatFullDate(call.ended_at)}
                      </Typography>
                    </Stack>
                  )}
                  <Typography level="body-sm">
                    Duration: {formatDuration(call.duration_seconds)}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Rep: {call.user_full_name}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* Notes */}
            {call.notes && (
              <Card variant="outlined" size="sm">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <NotesOutlined size={18} />
                    <Typography level="title-sm">Notes</Typography>
                  </Stack>
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    {call.notes}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Outcome Notes */}
            {call.outcome_notes && (
              <Card variant="outlined" size="sm">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Outcome Notes</Typography>
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    {call.outcome_notes}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Structured Notes */}
            {call.structured_notes && Object.keys(call.structured_notes).some(k => call.structured_notes[k]) && (
              <Card variant="outlined" size="sm">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Structured Notes</Typography>
                  <Stack spacing={1}>
                    {Object.entries(call.structured_notes).map(([key, value]) =>
                      value ? (
                        <Box key={key}>
                          <Typography level="body-xs" sx={{ fontWeight: 600, color: 'text.tertiary' }}>
                            {STRUCTURED_NOTE_LABELS[key] || key}
                          </Typography>
                          <Typography level="body-sm">{value}</Typography>
                        </Box>
                      ) : null
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Tags */}
            {call.tags && call.tags.length > 0 && (
              <Box>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>Tags</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {call.tags.map((tag, i) => (
                    <Chip key={i} size="sm" variant="outlined">{tag}</Chip>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Followed Up At */}
            {call.followed_up && call.followed_up_at && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Marked as followed up on {formatFullDate(call.followed_up_at)}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}

const callColumns: ResponsiveColumn<RecentCall>[] = [
  {key:'company',header:'Company',mobileVariant:'header',render:call=><Typography level="title-sm">{call.company_name || 'Unnamed company'}</Typography>},
  {key:'contact',header:'Contact',render:call=>call.contact_name || '—'},
  {key:'rep',header:'Rep',render:call=>call.user_full_name},
  {key:'outcome',header:'Outcome',render:call=><Chip size="sm" color={OUTCOME_COLORS[call.outcome || 'other'] || 'neutral'}>{OUTCOME_LABELS[call.outcome || 'other'] || call.outcome || '—'}</Chip>},
  {key:'duration',header:'Duration',render:call=>formatDuration(call.duration_seconds)},
  {key:'date',header:'Date',render:call=>formatDate(call.started_at)},
]
const leaderboardColumns: ResponsiveColumn<LeaderboardEntry>[] = [
  {key:'rep',header:'Rep',mobileVariant:'header',render:row=>row.full_name},
  {key:'calls',header:'Calls',render:row=>row.total_calls},
  {key:'meetings',header:'Meetings',render:row=>row.meetings_set},
  {key:'rate',header:'Rate',render:row=>`${row.meeting_rate}%`},
]
const dateInput = (value: Date) => `${value.getFullYear()}-${String(value.getMonth()+1).padStart(2,'0')}-${String(value.getDate()).padStart(2,'0')}`

// ─── Follow Up Tracker Tab ──────────────────────────────────────────────────

function FollowUpTracker({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) {
  const [followedUpFilter, setFollowedUpFilter] = useState<string>('all')
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const toggleMutation = useToggleFollowedUp()

  const params: Record<string, string | undefined> = {
    date_from: dateFrom,
    date_to: dateTo,
  }
  if (followedUpFilter !== 'all') params.followed_up = followedUpFilter as 'true' | 'false'
  if (outcomeFilter !== 'all') params.outcome = outcomeFilter
  if (search) params.q = search

  const { data, isLoading, error, refetch } = useCallBuddyFollowUps(params, Boolean(dateFrom && dateTo && dateFrom <= dateTo))

  const handleRowClick = (call: FollowUpCall) => {
    setSelectedCallId(call.id)
    setDrawerOpen(true)
  }

  const pendingCount = data?.calls.filter(c => !c.followed_up).length ?? 0
  const completedCount = data?.calls.filter(c => c.followed_up).length ?? 0

  return (
    <>
      {/* Summary chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip variant="soft" color="warning" size="lg">
          {pendingCount} Pending Follow-Ups
        </Chip>
        <Chip variant="soft" color="success" size="lg">
          {completedCount} Completed
        </Chip>
        <Chip variant="soft" color="neutral" size="lg">
          {data?.total ?? 0} Total
        </Chip>
      </Stack>

      {toggleMutation.error && <Alert color="danger" sx={{mb:2}}>The follow-up status could not be saved. Please try again.</Alert>}
      {/* Filters */}
      <CollapsibleFilters><Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Select
          size="sm"
          value={followedUpFilter}
          onChange={(_, val) => setFollowedUpFilter(val || 'all')}
          sx={{ minWidth: 160 }}
        >
          <Option value="all">All Status</Option>
          <Option value="false">Not Followed Up</Option>
          <Option value="true">Followed Up</Option>
        </Select>
        <Select
          size="sm"
          value={outcomeFilter}
          onChange={(_, val) => setOutcomeFilter(val || 'all')}
          sx={{ minWidth: 160 }}
        >
          <Option value="all">All Outcomes</Option>
          <Option value="follow_up">Follow Up</Option>
          <Option value="meeting_set">Meeting Set</Option>
        </Select>
        <Input
          size="sm"
          placeholder="Search company, contact, notes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack></CollapsibleFilters>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert color="danger" endDecorator={<Button onClick={()=>refetch()}>Retry</Button>}>Failed to load follow-up calls.</Alert>
      ) : (
        <ResponsiveTable<FollowUpCall> rows={data?.calls ?? []} getRowKey={call=>call.id} columns={callColumns}
          emptyState={<Typography sx={{p:2}}>No follow-up calls found.</Typography>}
          rowActions={call=><Stack direction="row" spacing={1} alignItems="center"><Checkbox label="Done" checked={call.followed_up} disabled={toggleMutation.isPending} onChange={()=>toggleMutation.mutate({id:call.id,followed_up:!call.followed_up})}/><Button size="sm" variant="outlined" onClick={()=>handleRowClick(call)}>Details</Button></Stack>}/>

      )}

      <CallDetailDrawer
        callId={selectedCallId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  )
}

// ─── Main Reports Page ──────────────────────────────────────────────────────

export default function CallBuddyReports() {
  const today = new Date()
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const [dateFrom, setDateFrom] = useState(dateInput(firstOfMonth))
  const [dateTo, setDateTo] = useState(dateInput(today))
  const [tabValue, setTabValue] = useState(0)
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const validDates = Boolean(dateFrom && dateTo && dateFrom <= dateTo)
  const { data, isLoading, error, refetch } = useCallBuddyOverview({ date_from: dateFrom, date_to: dateTo }, validDates)

  return (
    <Box sx={{ p: {xs:0,md:2}, maxWidth: 1200, mx: 'auto', minWidth:0 }}>
      <Stack direction={{xs:"column",md:"row"}} justifyContent="space-between" alignItems={{xs:"stretch",md:"center"}} sx={{ mb: 3, gap:2 }}>
        <Typography level="h2">CallBuddy Reports</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap:"wrap",rowGap:1}}>
          <Input
            type="date"
            slotProps={{input:{"aria-label":"Report start date",max:dateTo || undefined}}}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="sm"
          />
          <Typography level="body-sm">to</Typography>
          <Input
            type="date"
            slotProps={{input:{"aria-label":"Report end date",min:dateFrom || undefined}}}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="sm"
          />
        </Stack>
      </Stack>

      {!validDates && <Alert color="warning" sx={{mb:2}}>Choose a start and end date, with the start on or before the end.</Alert>}
      <Tabs value={tabValue} onChange={(_, val) => setTabValue(val as number)} sx={{ mb: 3 }}>
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Follow-Up Tracker</Tab>
        </TabList>

        {/* ─── Overview Tab ─── */}
        <TabPanel value={0} sx={{ px: 0 }}>
          {!validDates ? null : isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert color="danger" endDecorator={<Button onClick={()=>refetch()}>Retry</Button>}>Failed to load CallBuddy reports.</Alert>
          ) : !data ? null : (
            <>
              {/* KPI Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={12} sm={6} md={3}>
                  <Card variant="soft" color="primary">
                    <CardContent>
                      <Typography level="body-sm">Total Calls</Typography>
                      <Typography level="h3" sx={{ my: 0.5 }}>{data.kpis.total_calls}</Typography>
                      <Typography level="body-xs">{data.kpis.calls_today} today</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Card variant="soft" color="success">
                    <CardContent>
                      <Typography level="body-sm">Meetings Set</Typography>
                      <Typography level="h3" sx={{ my: 0.5 }}>{data.kpis.meetings_set}</Typography>
                      <Typography level="body-xs">{data.kpis.meeting_rate}% meeting rate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Card variant="soft" color="neutral">
                    <CardContent>
                      <Typography level="body-sm">Avg Duration</Typography>
                      <Typography level="h3" sx={{ my: 0.5 }}>{formatDuration(data.kpis.avg_duration_seconds)}</Typography>
                      <Typography level="body-xs">per call</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Card variant="soft" color="warning">
                    <CardContent>
                      <Typography level="body-sm">This Week</Typography>
                      <Typography level="h3" sx={{ my: 0.5 }}>{data.kpis.calls_this_week}</Typography>
                      <Typography level="body-xs">calls made</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Outcome Breakdown Chart */}
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>Outcome Breakdown</Typography>
                      {data.outcomes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart
                            data={data.outcomes.map((o) => ({
                              name: OUTCOME_LABELS[o.outcome || 'other'] || o.outcome || 'Unknown',
                              count: o.count,
                            }))}
                            layout="vertical"
                            margin={{ left: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#0B6BCB" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Typography level="body-sm" sx={{ textAlign: 'center', py: 4 }}>
                          No calls in this period.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Leaderboard */}
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>Leaderboard</Typography>
                      <ResponsiveTable rows={data.leaderboard} getRowKey={row=>row.user_id} columns={leaderboardColumns} emptyState={<Typography sx={{p:2}}>No calls in this period.</Typography>}/>

                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Recent Calls Table */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-md" sx={{ mb: 2 }}>Recent Calls</Typography>
                  <ResponsiveTable rows={data.recent_calls} getRowKey={call=>call.id} columns={callColumns} emptyState={<Typography sx={{p:2}}>No calls in this period.</Typography>} rowActions={call=><Button size="sm" variant="outlined" onClick={()=>{setSelectedCallId(call.id);setDrawerOpen(true)}}>Details</Button>}/>

                </CardContent>
              </Card>
            </>
          )}
        </TabPanel>

        {/* ─── Follow-Up Tracker Tab ─── */}
        <TabPanel value={1} sx={{ px: 0 }}>
          {validDates && <FollowUpTracker dateFrom={dateFrom} dateTo={dateTo} />}
        </TabPanel>
      </Tabs>

      {/* Drawer for overview tab recent calls */}
      <CallDetailDrawer
        callId={selectedCallId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  )
}
